import { StrictMode, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { AppIntlProvider } from "./i18n/intl-provider";
import { ThemeProvider } from "./providers/ThemeProvider";
import { ToastProvider } from "./providers/ToastProvider";
import { QueryProvider } from "./providers/query-provider";
import { TooltipProvider } from "./components/ui/tooltip/Tooltip";
import { BackendConnectionModal } from "./components/backend-health/BackendConnectionModal";
import { routeTree } from "./routeTree.gen";
import "./styles/globals.css";

const enableTanStackDevtools =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_TANSTACK_DEVTOOLS === "true";

// React Query devtools stay opt-in so local debug affordances do not cover app UI.
const ReactQueryDevtools = enableTanStackDevtools
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null;

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("#root container missing in index.html");
}

// Provider order:
//   ThemeProvider    — applies dark-mode class to <html> early.
//   QueryProvider    — TanStack Query client.
//   AppIntlProvider  — locale + messages context.
//   TooltipProvider  — Radix tooltip delay (700ms) for all tooltips.
//   RouterProvider   — TanStack Router (wraps app body).
//   ToastProvider    — Sonner Toaster portal sibling.
createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <QueryProvider>
        <AppIntlProvider>
          <TooltipProvider delayDuration={700}>
            <RouterProvider router={router} />
            <BackendConnectionModal />
            <ToastProvider />
            {enableTanStackDevtools && (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}
          </TooltipProvider>
        </AppIntlProvider>
      </QueryProvider>
    </ThemeProvider>
  </StrictMode>,
);
